import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { Pause, Pencil, Play, Plus, Trash2 } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  createPartner,
  listPartners,
  updatePartner,
  type Partner,
} from "@/services/platformPartnersService";

const PARTNERS_QUERY_KEY = ["platform", "partners"] as const;

/** Mirrors apps/backend/src/auth/plans.config.ts's current (non-legacy) tiers. */
const PLAN_OPTIONS = [
  { id: "literalE", label: "Literal E" },
  { id: "semilla", label: "Semilla" },
  { id: "tala", label: "Tala" },
  { id: "ombu", label: "Ombú" },
  { id: "ceibo", label: "Ceibo" },
] as const;

const stageFormSchema = z.object({
  discountType: z.enum(["flat", "percent"]),
  discountValue: z.coerce.number().int().positive(),
  durationMonths: z.coerce.number().int().positive().max(60),
});

const partnerFormSchema = z.object({
  name: z.string().trim().min(1),
  code: z
    .string()
    .trim()
    .min(1)
    .regex(/^[A-Za-z0-9_-]+$/),
  stages: z.array(stageFormSchema).min(1),
  applicablePlanIds: z.array(z.string()).optional(),
  usageCap: z.coerce.number().int().positive().optional().or(z.literal("")),
  notes: z.string().optional(),
});

type PartnerFormValues = z.infer<typeof partnerFormSchema>;

const EMPTY_STAGE: PartnerFormValues["stages"][number] = {
  discountType: "percent",
  discountValue: 20,
  durationMonths: 3,
};

const EMPTY_FORM_VALUES: PartnerFormValues = {
  name: "",
  code: "",
  stages: [EMPTY_STAGE],
  applicablePlanIds: [],
  usageCap: "",
  notes: "",
};

function stageAmountLabel(
  stage: Partner["stages"][number],
): string {
  return stage.discountType === "percent"
    ? `${stage.discountValue}%`
    : `$${(stage.discountValue / 100).toLocaleString("es-UY", {
        minimumFractionDigits: 2,
      })}`;
}

function discountLabel(
  partner: Partner,
  t: (k: string, d: string, o?: Record<string, unknown>) => string,
): string {
  return partner.stages
    .map((stage) =>
      t("partners.discountSummary", "{{amount}} off for {{months}} months", {
        amount: stageAmountLabel(stage),
        months: stage.durationMonths,
      }),
    )
    .join(" → ");
}

export function PartnersPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: PARTNERS_QUERY_KEY,
    queryFn: listPartners,
  });

  const form = useForm<PartnerFormValues>({
    resolver: zodResolver(partnerFormSchema),
    defaultValues: EMPTY_FORM_VALUES,
  });
  const stageArray = useFieldArray({ control: form.control, name: "stages" });

  const openCreateDialog = (): void => {
    setEditingPartner(null);
    form.reset(EMPTY_FORM_VALUES);
    setDialogOpen(true);
  };

  const openEditDialog = (partner: Partner) => (): void => {
    setEditingPartner(partner);
    form.reset({
      name: partner.name,
      code: partner.code,
      stages: partner.stages,
      applicablePlanIds: partner.applicablePlanIds ?? [],
      usageCap: partner.usageCap ?? "",
      notes: partner.notes ?? "",
    });
    setDialogOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: (values: PartnerFormValues) => {
      const payload = {
        name: values.name,
        code: values.code,
        stages: values.stages,
        applicablePlanIds:
          values.applicablePlanIds && values.applicablePlanIds.length > 0
            ? values.applicablePlanIds
            : null,
        usageCap: values.usageCap === "" ? null : Number(values.usageCap),
        notes: values.notes?.trim() || null,
      };
      return editingPartner
        ? updatePartner(editingPartner.id, payload)
        : createPartner(payload);
    },
    onSuccess: () => {
      toast.success(
        editingPartner
          ? t("partners.updated", "Partner updated.")
          : t("partners.created", "Partner created."),
      );
      setDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: PARTNERS_QUERY_KEY });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : t("partners.saveError", "Couldn't save this partner.");
      toast.error(message);
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (partner: Partner) =>
      updatePartner(partner.id, {
        status: partner.status === "active" ? "paused" : "active",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PARTNERS_QUERY_KEY });
    },
    onError: () => {
      toast.error(t("partners.toggleError", "Couldn't update this partner's status."));
    },
  });

  const partners = useMemo(() => data ?? [], [data]);

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("partners.title", "Partners")}
        description={t(
          "partners.description",
          "Institutions with a shared signup code that gives their members a time-boxed discount.",
        )}
        actions={
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4" aria-hidden />
            {t("partners.new", "New partner")}
          </Button>
        }
      />

      <TableCard>
        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : isError ? (
          <EmptyState
            title={t("partners.errorTitle", "Couldn't load partners")}
            message={t("partners.errorMessage", "Try refreshing the page.")}
          />
        ) : partners.length === 0 ? (
          <EmptyState
            title={t("partners.emptyTitle", "No partners yet")}
            message={t(
              "partners.emptyMessage",
              "Create a partner to generate a shared signup code with a discount.",
            )}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("partners.col.name", "Name")}</TableHead>
                <TableHead>{t("partners.col.code", "Code")}</TableHead>
                <TableHead>{t("partners.col.discount", "Discount")}</TableHead>
                <TableHead>{t("partners.col.usage", "Redemptions")}</TableHead>
                <TableHead>{t("partners.col.collected", "Collected")}</TableHead>
                <TableHead>{t("partners.col.status", "Status")}</TableHead>
                <TableHead className="w-[60px]" aria-hidden />
              </TableRow>
            </TableHeader>
            <TableBody>
              {partners.map((partner) => {
                const actions: TableAction[] = [
                  {
                    label: t("partners.edit", "Edit"),
                    icon: Pencil,
                    onClick: openEditDialog(partner),
                  },
                  {
                    label:
                      partner.status === "active"
                        ? t("partners.pause", "Pause")
                        : t("partners.activate", "Activate"),
                    icon: partner.status === "active" ? Pause : Play,
                    onClick: () => toggleStatusMutation.mutate(partner),
                  },
                ];
                return (
                  <TableRow key={partner.id}>
                    <TableCell className="font-medium">{partner.name}</TableCell>
                    <TableCell>
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                        {partner.code}
                      </code>
                    </TableCell>
                    <TableCell>
                      <span className="block max-w-[260px] truncate" title={discountLabel(partner, t)}>
                        {discountLabel(partner, t)}
                      </span>
                      {partner.applicablePlanIds != null ? (
                        <span className="block text-xs text-muted-foreground">
                          {partner.applicablePlanIds.join(", ")}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {partner.redemptionCount}
                      {partner.usageCap != null ? ` / ${partner.usageCap}` : ""}
                    </TableCell>
                    <TableCell>
                      <span className="block">
                        ${(partner.collectedMinor / 100).toLocaleString("es-UY", {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                      {partner.pendingChargeCount > 0 ? (
                        <span className="block text-xs text-muted-foreground">
                          {t("partners.pendingCount", "{{count}} pending", {
                            count: partner.pendingChargeCount,
                          })}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <StatusBadge variant={partner.status === "active" ? "success" : "neutral"}>
                        {partner.status === "active"
                          ? t("partners.status.active", "Active")
                          : t("partners.status.paused", "Paused")}
                      </StatusBadge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TableActionsMenu actions={actions} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableCard>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPartner
                ? t("partners.edit", "Edit")
                : t("partners.new", "New partner")}
            </DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4 max-h-[70vh] overflow-y-auto pr-1"
            onSubmit={form.handleSubmit((values) => saveMutation.mutate(values))}
          >
            <div className="space-y-1.5">
              <Label htmlFor="partner-name">{t("partners.form.name", "Name")}</Label>
              <Input id="partner-name" {...form.register("name")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="partner-code">{t("partners.form.code", "Signup code")}</Label>
              <Input id="partner-code" {...form.register("code")} placeholder="AJE2026" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("partners.form.stages", "Discount stages")}</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => stageArray.append(EMPTY_STAGE)}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  {t("partners.form.addStage", "Add stage")}
                </Button>
              </div>
              {stageArray.fields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
                  <div className="space-y-1">
                    {index === 0 ? (
                      <Label className="text-xs">
                        {t("partners.form.discountType", "Type")}
                      </Label>
                    ) : null}
                    <Controller
                      control={form.control}
                      name={`stages.${index}.discountType`}
                      render={({ field: selectField }) => (
                        <Select value={selectField.value} onValueChange={selectField.onChange}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percent">
                              {t("partners.form.percent", "% off")}
                            </SelectItem>
                            <SelectItem value="flat">
                              {t("partners.form.flat", "Flat (UYU cents)")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div className="space-y-1">
                    {index === 0 ? (
                      <Label className="text-xs">
                        {t("partners.form.discountValue", "Value")}
                      </Label>
                    ) : null}
                    <Input
                      type="number"
                      {...form.register(`stages.${index}.discountValue`)}
                    />
                  </div>
                  <div className="space-y-1">
                    {index === 0 ? (
                      <Label className="text-xs">
                        {t("partners.form.duration", "Months")}
                      </Label>
                    ) : null}
                    <Input
                      type="number"
                      {...form.register(`stages.${index}.durationMonths`)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={stageArray.fields.length <= 1}
                    onClick={() => stageArray.remove(index)}
                    aria-label={t("partners.form.removeStage", "Remove stage")}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">
                {t(
                  "partners.form.stagesHint",
                  "Applied in order — e.g. 100% off for 3 months, then 50% off for 3 months, then full price.",
                )}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t("partners.form.applicablePlans", "Applies to plans")}</Label>
              <Controller
                control={form.control}
                name="applicablePlanIds"
                render={({ field }) => (
                  <div className="grid grid-cols-2 gap-2">
                    {PLAN_OPTIONS.map((plan) => {
                      const checked = field.value?.includes(plan.id) ?? false;
                      return (
                        <label
                          key={plan.id}
                          className="flex items-center gap-2 text-sm"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(next) => {
                              const current = field.value ?? [];
                              field.onChange(
                                next
                                  ? [...current, plan.id]
                                  : current.filter((id) => id !== plan.id),
                              );
                            }}
                          />
                          {plan.label}
                        </label>
                      );
                    })}
                  </div>
                )}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "partners.form.applicablePlansHint",
                  "None selected = applies to any plan.",
                )}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="partner-cap">
                {t("partners.form.usageCap", "Usage cap (optional)")}
              </Label>
              <Input id="partner-cap" type="number" {...form.register("usageCap")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="partner-notes">{t("partners.form.notes", "Notes")}</Label>
              <Textarea id="partner-notes" rows={3} {...form.register("notes")} />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saveMutation.isPending}>
                {t("partners.save", "Save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
